"use client"

import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { Plus } from "lucide-react"

import { cn } from "@/lib/utils"

const Accordion = AccordionPrimitive.Root

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      className={cn(
        "overflow-hidden rounded-[var(--radius-lg)] border border-[var(--hairline-strong)]",
        "bg-[var(--surface)] shadow-xs transition-[border-color,box-shadow] duration-200",
        "hover:border-brand-500/30",
        "data-[state=open]:border-brand-500/45 data-[state=open]:shadow-sm",
        className,
      )}
      {...props}
    />
  )
}

function AccordionTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        className={cn(
          "group flex flex-1 items-center justify-between gap-4 p-5 text-start",
          "text-[0.9375rem] font-bold text-[var(--content)]",
          "transition-colors hover:text-brand-600 dark:hover:text-brand-300",
          className,
        )}
        {...props}
      >
        {children}
        {/* Rotates to an ×; direction-agnostic so RTL needs no special case. */}
        <Plus
          className="size-5 shrink-0 text-brand-500 transition-transform duration-300 ease-[var(--ease-out-quint)] group-data-[state=open]:rotate-135"
          aria-hidden
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      className={cn(
        "overflow-hidden",
        "data-[state=closed]:animate-[accordion-up_220ms_var(--ease-out-quint)]",
        "data-[state=open]:animate-[accordion-down_260ms_var(--ease-out-quint)]",
      )}
      {...props}
    >
      <div className={cn("px-5 pb-5 text-[0.9375rem] leading-[var(--leading-relaxed)] text-[var(--content-muted)]", className)}>
        {children}
      </div>
    </AccordionPrimitive.Content>
  )
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
